import React, { useState } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
// import { name, random } from 'faker';
import gql from 'graphql-tag';
import { FetchPolicy } from 'apollo-client';
import { TextField, CircularProgress } from '@material-ui/core';
import { IAutoCompleteItem } from '../typings/autoComplete';
import { showNotification } from '../App';
import { InfiniteAutocomplete } from './InfiniteAutocomplete';
import { client } from '..';
import { RenderInputParams } from '@material-ui/lab';
import { useDebouncedCallback } from 'use-debounce/lib';
import { getInitials } from '../feeEarnerDashboard/selectors/UserAssistantSelector';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
        minWidth: '200px',
    },
    textField: {
        width: '100%',
    },
    iconSelected: {
        width: 17,
        height: 17,
        marginRight: 5,
        marginLeft: -2,
      },
      color: {
        width: 14,
        height: 14,
        flexShrink: 0,
        borderRadius: 3,
        marginRight: 8,
        marginTop: 2,
      },
      text: {
        flexGrow: 1,
      },
      close: {
        opacity: 0.6,
        width: 18,
        height: 18,
      },
      renderWrapper: {
          padding: '15px 0'
      },
      customRenderWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        minWidth: 0,
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        '& .avatar': {
            flex: '0 0 auto',
            width: 34,
            height: 34,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.02em',
            backgroundColor: '#eef0f6',
            color: '#667085',
        },
        // a staff member: the accent colours, so staff stand out from contacts without a staff record
        '& .avatar.staff-member': {
            backgroundColor: 'rgba(22, 103, 199, 0.12)',
            color: '#1667c7',
        },
        '& .other': {
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexFlow: 'column',
        },
        '& .top': {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minWidth: 0,
        },
        '& .primary': {
            flex: 1,
            minWidth: 0,
            fontSize: '0.9375rem',
            fontWeight: 600,
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        '& .role': {
            flex: '0 0 auto',
            maxWidth: '45%',
            padding: '1px 8px',
            borderRadius: 999,
            backgroundColor: '#f2f4fa',
            color: '#667085',
            fontSize: '0.6875rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        '& .secondary': {
            color: 'rgba(0, 0, 0, 0.54)',
            fontSize: '0.8125rem',
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
      }
  })
);

interface UserAutocompleteSelectorProps {
    isStaffOnly?: boolean;
    multiple?: boolean;
    isExternal?: boolean;
    label?: string;
    name: string;
    required?: boolean;
    // tslint:disable-next-line: no-any
    value?: IAutoCompleteItem | any; // value only available in single select
    onSelection?: (value: IAutoCompleteItem | IAutoCompleteItem[], name: string) => void;
    id?: string;
    visible?: {
        role: boolean;
    };
    error?: boolean;
    disablePortal?: boolean;
    className?: string;
    helperText?: React.ReactNode;
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
    isAuthorized?: boolean;
    variant?: 'outlined' | 'filled' | 'standard';
    size?: 'small' | 'medium';
}

interface UserAutocompleteSelectorState {
    hasNextPage: boolean;
    isNextPageLoading: boolean;
    offset: number;
    filter: string;
    // tslint:disable-next-line: no-any
    items: any;
}

export const UserAutocompleteSelector: React.FC<UserAutocompleteSelectorProps> = props => {

    const classes = useStyles();
    const loadBlockLimit = 20;
    
    const [state, setState] = useState<UserAutocompleteSelectorState>({
        hasNextPage: true,
        isNextPageLoading: false,
        offset: 0,
        filter: '',
        items: [],
    });

    const [delayedonInputChange] = useDebouncedCallback(
        // function
        (event: React.ChangeEvent<{}>, value: string, reason: 'input' | 'reset' | 'clear') => {

            let isClear = false;
            let offset = 0;
            let filter = '';

            if (reason === 'input') {
                filter = value;
                isClear = true;

                fetchData(filter, offset, isClear);
            }
        },
        // delay in ms
        500
    );
    
    const fetchData = (filter: string, offset: number, isClear: boolean) => {

        setState((prevState) => {
            return {
                ...prevState,
                isNextPageLoading: true
            };
        });

        const userQueryParams: UserQueryParams = {
            filter: filter,
            first: loadBlockLimit,
            offset: offset,
            isStaff: props.isStaffOnly !== undefined ? props.isStaffOnly : undefined,
            isExternal: props.isExternal !== undefined ? props.isExternal : undefined,
            isAuthorized: props.isAuthorized
        };

        retrieveUserListData(
            userQueryParams,
            false, // this.state.isRefresh, -- Since we are updating and creating, we do not fetch cached data
            // tslint:disable-next-line: no-console
            (data) => onRetrieveUserList(data, filter, offset, isClear),
            // tslint:disable-next-line:no-any
            function (reason: any): void {
                showNotification(null, reason, 'error');

                setState((prevState) => {
                    return {
                        ...prevState,
                        isNextPageLoading: false,
                    };
                });
            }
        );
    };

    const onRetrieveUserList = (data: UserListData, filter: string, offset: number, isClear: boolean) => {

        const userData = data.administrator.users.user;
        const recordCount = data.administrator.users.recordCount;

        // tslint:disable-next-line: no-any
        let itemsList: any = [];

        let items = state.items;

        if (!isClear) {
            itemsList = [...state.items];
        } else {
            items = [];
        }

        if (items.length === 0) {
            itemsList = userData.map( (source: User) => ({
                value: source.userID, 
                label: source.isStaff ? source.staffName : source.friendlyName,
                email: source.email,
                roleName: source.roleName
            }));
        } else {
            for (const user of userData) {
                // tslint:disable-next-line: no-any
                const isExists = items.filter((item: any) => item.email.toLowerCase().includes(user.email)).length;

                if (isExists === 0) {
                    itemsList.push({
                        label: user.isStaff ? user.staffName : user.friendlyName,
                        value: user.userID,
                        isStaff: user.isStaff,
                        email: user.email,
                        roleName: user.roleName
                    });
                }
            }
        }

        setState((prevState) => {
            return {
                ...prevState,
                filter: filter,
                isNextPageLoading: false,
                items: itemsList,
                offset: offset + loadBlockLimit,
                hasNextPage: userData.length !== 0 && (offset + loadBlockLimit) < recordCount,
            };
        });

    };

    // tslint:disable-next-line: no-any
    const loadNextPage = (...args: any) => {
        
        if (state.hasNextPage && !state.isNextPageLoading) {
            fetchData(state.filter, state.offset, false);
        }
    };

    const onClose = (event: React.ChangeEvent<{}>) => {

        setState((prevState) => {
            return {
                ...prevState,
                isNextPageLoading: false,
                hasNextPage: true,
                filter: '',
                offset: 0,
                items: []
            };
        });
    };

    const onOpen = (event: React.ChangeEvent<{}>) => {
        
        fetchData(state.filter, 0, true);
    };

    const onSelection = (selection: IAutoCompleteItem, name: string) => {

        if (props.onSelection) {
            props.onSelection(selection, name);
        }
    };

    const getInputProps = (params: RenderInputParams, isLoading?: boolean) => {
        return  ({   
            ...params.InputProps,
            endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),     
         });
    };

    return (
        <div className={`${classes.root} ${props.className}`}>
            <InfiniteAutocomplete
                id={props.id}
                itemSize={58}
                loading={state.isNextPageLoading}
                disablePortal={props.disablePortal !== undefined ? props.disablePortal : true}
                name={props.name}
                isMultiple={props.multiple}
                value={props.value}
                hasNextPage={state.hasNextPage}
                isNextPageLoading={state.isNextPageLoading}
                items={state.items}
                loadNextPage={loadNextPage}
                // disableCloseOnSelect={true}
                className={classes.textField}
                disableListWrap={true}
                onSelection={onSelection}
                onClose={onClose}
                onOpen={onOpen}
                getOptionLabel={(option: IAutoCompleteItem) => option.label}
                getOptionSelected={(option, value) => value.value === option.value}
                onInputChange={delayedonInputChange}
                onBlur={props.onBlur}
                size={props.size ? props.size : "medium"}
                ChipProps={{
                    size: props.size ?? 'medium'
                }}
                renderOption={(option, { selected }) => {
                    return (
                        <div className={classes.customRenderWrapper}>
                            <div className={`avatar${option.isStaff ? ' staff-member' : ''}`}>
                                {getInitials(option.label)}
                            </div>
                            <div className="other">
                                <div className="top">
                                    <div className="primary" title={option.label}>{option.label}</div>
                                    {!!props.visible && props.visible!.role && !!option.roleName && (
                                        <div className="role" title={option.roleName}>{option.roleName}</div>
                                    )}
                                </div>
                                <div className="secondary" title={option.email}>{option.email}</div>
                            </div>
                        </div>
                    );
                }}
                renderInput={params => (
                    <TextField
                        {...params}
                        variant={props.variant ? props.variant : 'standard'}
                        size={props.size ? props.size : 'medium'}
                        label={props.label}
                        required={props.required}
                        error={props.error}
                        fullWidth={true}
                        InputProps={getInputProps(params, state.isNextPageLoading)}
                        helperText={props.helperText}
                    />
                )}
            />
        </div>
    );
};

export function retrieveUserListData(
    query: UserQueryParams,
    refreshData: boolean,
    onSuccess: (data: UserListData) => void,
    // tslint:disable-next-line:no-any
    onError: (reason: any) => void
): void {
    var fetchPolicy: FetchPolicy = refreshData === true ? 'network-only' : 'cache-first';
    client
        .query({
            query: UserListQuery,
            variables: {
                filter: query.filter,
                first: query.first,
                offset: query.offset,
                isStaff: query.isStaff,
                isExternal: query.isExternal,
                isAuthorized: query.isAuthorized
            },
            fetchPolicy: fetchPolicy
        })
        // tslint:disable-next-line:no-any
        .then((results: { data: any }) => {
            onSuccess(results.data);
        })
        // tslint:disable-next-line:no-any
        .catch((reason: any) => {
            onError(reason);
        });
}

export interface UserQueryParams {
    offset?: number; 
    first?: number;
    filter?: string;
    isStaff?: boolean | null;
    isExternal?: boolean;
    isAuthorized?: boolean;
}

export interface UserListData {
    loading?: boolean;
    networkStatus?: number;
    administrator: Administrator;
}

export interface Administrator {
    users: UserList;    
}

export interface UserList {
    recordCount: number;
    user: User[];
}

export interface User {
    userID: string;
    email: string;
    friendlyName: string;
    roleName: boolean;
    isStaff: boolean;
    staffName: string;
    isExternal: boolean;
    hasTasks: boolean;
    assistantCount: number;
}

const UserListQuery = gql`
    query userQuery($offset: Int, $first: Int, $filter: String, $isStaff: Boolean, $isExternal: Boolean, $isAuthorized: Boolean){
        administrator{
            users(offset:$offset, first:$first, filter:$filter, isStaff: $isStaff, isExternal: $isExternal, isAuthorized: $isAuthorized){
                recordCount,
                user{
                    userID,
                    email,
                    friendlyName,
                    roleName
                    isStaff,
                    staffName,
                    isExternal,
                    hasTasks,
                    assistantCount
                }
            }
        }
    }
`;